import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Sparkles, ChevronRight } from 'lucide-react';
import { EditableImage, EditableText, EditableList, PLACEHOLDER_IMG } from './EditableComponents';
import { useEditMode } from '../contexts/EditModeContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SvapoProducts = () => {
  const { isEditMode } = useEditMode();
  const { toast } = useToast();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      if (response.data && response.data.length > 0) {
        setProducts(response.data);
      } else {
        // Fallback ai dati mock se non ci sono prodotti
        setProducts([
          {
            id: "1",
            brand: "KIWI",
            category: "Pods & Resistenze",
            products: ["KIWI GO+", "KIWI AIR", "KIWI 0% NICOTINA", "KIWI IWIK", "KIWI LIQUID", "KIWI SPARK", "KIWI 2"],
            image: "https://images.unsplash.com/photo-1585123334904-845d60e97a29?w=500&q=80",
            featured: true
          },
          {
            id: "2",
            brand: "ELFBAR",
            category: "Pods & Resistenze",
            products: ["ELFBAR ELFA", "LOST MARY TOCA", "ELFBAR MASTER", "ELFLIQ Liquid"],
            image: "https://images.unsplash.com/photo-1593640407042-61cb2c89e44d?w=500&q=80",
            featured: true
          },
          {
            id: "3",
            brand: "RELX",
            category: "Kit & Pods",
            products: ["ESSENTIAL KIT", "INFINITY 2 PLUS KIT", "PRO PODS", "2 in 1"],
            image: "https://images.unsplash.com/photo-1606657041112-be379dd15e0b?w=500&q=80",
            featured: true
          }
        ]);
      }
    } catch (error) {
      // Failed to load - use empty array
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const saveProductImage = async (productId, newImageUrl) => {
    try {
      await axios.put(`${API}/products/${productId}`, { image: newImageUrl });
      setProducts(products.map(p => 
        p.id === productId ? { ...p, image: newImageUrl } : p
      ));
      if (selectedProduct?.id === productId) {
        setSelectedProduct({ ...selectedProduct, image: newImageUrl });
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile salvare l'immagine del prodotto",
        variant: "destructive"
      });
    }
  };

  const saveProductBrand = async (productId, newBrand) => {
    try {
      await axios.put(`${API}/products/${productId}`, { brand: newBrand });
      setProducts(products.map(p => 
        p.id === productId ? { ...p, brand: newBrand } : p
      ));
      // Aggiorna anche il prodotto selezionato se aperto
      if (selectedProduct?.id === productId) {
        setSelectedProduct({ ...selectedProduct, brand: newBrand });
      }
    } catch (error) {
      console.error('Error updating product brand:', error);
    }
  };

  const saveProductCategory = async (productId, newCategory) => {
    try {
      await axios.put(`${API}/products/${productId}`, { category: newCategory });
      setProducts(products.map(p => 
        p.id === productId ? { ...p, category: newCategory } : p
      ));
      // Aggiorna anche il prodotto selezionato se aperto
      if (selectedProduct?.id === productId) {
        setSelectedProduct({ ...selectedProduct, category: newCategory });
      }
    } catch (error) {
      // Category update failed
    }
  };

  const saveProductsList = async (productId, newProductsList) => {
    try {
      await axios.put(`${API}/products/${productId}`, { products: newProductsList });
      setProducts(products.map(p => 
        p.id === productId ? { ...p, products: newProductsList } : p
      ));
      // Aggiorna anche il prodotto selezionato se aperto
      if (selectedProduct?.id === productId) {
        setSelectedProduct({ ...selectedProduct, products: newProductsList });
      }
    } catch (error) {
      // Products list update failed - state will revert
      setProducts(products);
    }
  };

  // Mostra TUTTI i prodotti di default (non solo featured)
  const displayedProducts = products;

  if (loading) {
    return (
      <section id="svapo" className="section-padding">
        <div className="container">
          <p className="text-center">Caricamento prodotti...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="svapo" className="section-padding">
      <div className="container">
        <div className="section-header">
          <Badge className="section-badge">I Nostri Prodotti</Badge>
          <h2 className="section-title">Svapo & E-Cigarettes</h2>
          <p className="section-description">
            Ampia selezione dei migliori brand di sigarette elettroniche, pod e liquidi
          </p>
        </div>

        <div className="products-grid">
          {displayedProducts.map((product) => (
            <Card 
              key={product.id} 
              className="product-card"
              onClick={() => { if (!isEditMode) setSelectedProduct(product); }}
              style={{ cursor: isEditMode ? 'default' : 'pointer' }}
            >
              <div className="product-image-container">
                {isEditMode ? (
                  <EditableImage
                    src={product.image}
                    alt={product.brand}
                    onSave={(url) => saveProductImage(product.id, url)}
                    label={`Immagine ${product.brand}`}
                    className="product-image"
                  />
                ) : (
                  <>
                    <img 
                      src={product.image || PLACEHOLDER_IMG} 
                      alt={product.brand}
                      className="product-image"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMG; }}
                    />
                    {product.featured && (
                      <div className="product-featured-badge">
                        <Sparkles className="w-3 h-3" />
                        <span>Popolare</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="product-content">
                {isEditMode ? (
                  <>
                    <EditableText
                      value={product.brand}
                      onSave={(val) => saveProductBrand(product.id, val)}
                      label="Brand"
                    />
                    <EditableText
                      value={product.category}
                      onSave={(val) => saveProductCategory(product.id, val)}
                      label="Categoria"
                    />
                  </>
                ) : (
                  <>
                    <h3 className="product-brand">{product.brand}</h3>
                    <p className="product-category">{product.category}</p>
                  </>
                )}
                <div className="product-items">
                  {product.products.slice(0, 2).map((item) => (
                    <Badge key={`${product.id}-${item}`} variant="secondary" className="product-item-badge">
                      {item}
                    </Badge>
                  ))}
                  {product.products.length > 2 && (
                    <Badge variant="outline" className="product-more-badge">
                      +{product.products.length - 2}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  className="product-details-btn"
                  data-testid={`product-details-btn-${product.id}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedProduct(product); }}
                >
                  {isEditMode ? 'Modifica prodotto' : 'Vedi dettagli'} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </Card>
          ))}
          
          {/* Pulsante Aggiungi Prodotto in Modalità Admin */}
          {isEditMode && (
            <Card 
              className="product-card"
              onClick={() => window.location.href = '/admin'}
              style={{ 
                cursor: 'pointer',
                border: '2px dashed #0891b2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                background: 'linear-gradient(135deg, #f0fdfa 0%, #cffafe 100%)'
              }}
            >
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '40px',
                  color: 'white',
                  fontWeight: 'bold'
                }}>
                  +
                </div>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: '#0891b2',
                  marginBottom: '8px'
                }}>
                  Aggiungi Prodotto
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#64748b'
                }}>
                  Clicca per aggiungere un nuovo brand
                </p>
              </div>
            </Card>
          )}
        </div>

        <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
          <DialogContent className="dialog-content" style={{ maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }}>
            {/* Pulsante X per chiudere su mobile */}
            <button
              onClick={() => setSelectedProduct(null)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                fontWeight: 'bold'
              }}
              aria-label="Chiudi"
            >
              ×
            </button>
            <DialogHeader>
              <DialogTitle className="dialog-title">
                {isEditMode ? (
                  <EditableText
                    value={selectedProduct?.brand || ''}
                    onSave={(val) => saveProductBrand(selectedProduct?.id, val)}
                    label="Brand"
                  />
                ) : (
                  selectedProduct?.brand
                )}
              </DialogTitle>
              <DialogDescription className="dialog-description">
                {isEditMode ? (
                  <EditableText
                    value={selectedProduct?.category || ''}
                    onSave={(val) => saveProductCategory(selectedProduct?.id, val)}
                    label="Categoria"
                  />
                ) : (
                  selectedProduct?.category
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="dialog-body">
              {isEditMode ? (
                <EditableImage
                  src={selectedProduct?.image}
                  alt={selectedProduct?.brand}
                  onSave={(url) => saveProductImage(selectedProduct?.id, url)}
                  label={`Immagine ${selectedProduct?.brand}`}
                />
              ) : (
                <img 
                  src={selectedProduct?.image || PLACEHOLDER_IMG} 
                  alt={selectedProduct?.brand}
                  className="dialog-image"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMG; }}
                />
              )}
              <div className="dialog-products">
                <h4 className="dialog-products-title">
                  Prodotti disponibili:
                </h4>
                <ul className="dialog-products-list">
                  {selectedProduct?.products.map((item) => (
                    <li key={`${selectedProduct.id}-detail-${item}`} className="dialog-product-item">
                      <ChevronRight className="w-4 h-4 text-cyan-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {isEditMode && (
                  <EditableList
                    items={selectedProduct?.products || []}
                    onSave={(newList) => saveProductsList(selectedProduct?.id, newList)}
                    label="Lista Prodotti"
                  />
                )}
              </div>
              <div className="dialog-footer">
                <p className="dialog-footer-text">
                  Vieni in negozio per scoprire prezzi e disponibilità
                </p>
                <Button className="dialog-cta" onClick={() => {
                  setSelectedProduct(null);
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}>
                  Contattaci
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};
